import moment from 'moment';
import { constants } from '@clubmed/utils';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import 'Components/frameworks/button/Button';
import { ICONS } from 'Components/utils/icon/icons';
import type { PriceTicketItem } from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { QUOTATION_FORM_SECTION_ID } from 'Components/widgets/product-quotation-form/productQuotationForm.constants';
import { t } from 'Src/i18n';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
const { PRODUCT_TYPES } = constants;
export const getPriceTicketButton = (product: Product): ButtonProps => {
  const props = {
    children: t('productPriceTicketLine.seeAllPrices'),
    iconLeft: {
      name: ICONS.DATE,
    },
    url: '#booking',
    trackingData: {
      actionGA: 'priceBar',
      labelGA: 'checkPrices',
    },
  };

  switch (product.type) {
    case PRODUCT_TYPES.TOUR:
      return {
        ...props,
        iconLeft: null,
        children: t('productPriceTicketLine.askForQuotation'),
        url: `#${QUOTATION_FORM_SECTION_ID}`,
      };

    case PRODUCT_TYPES.CRUISE:
      return { ...props, iconLeft: null, children: t('productPriceTicketLine.book') };

    default:
      return props;
  }
};
export const getPriceTicketSubtitle = (product: Product, cruise?: null | Cruise): string => {
  const { destination, type = '' } = product;
  const destinationCountry = (destination as { country?: string } | undefined)?.country;

  switch (type) {
    case PRODUCT_TYPES.CRUISE:
      return cruise?.fromToDestination || '';

    default:
      return destinationCountry || '';
  }
};

const getPriceTicketItem = (item: PriceTicketItem): PriceTicketItem | null =>
  item.longDesc || item.shortDesc ? item : null;

export const getPriceTicketItems = ({
  product,
  price,
  fromStore,
}: {
  product: Product;
  price: ProductPrice;
  fromStore: any;
}): Array<PriceTicketItem> => {
  const { type } = product;
  const duration = fromStore.duration || product.duration || '';

  /* departureDate */
  const departureDateValue = fromStore.departureDate
    ? moment(fromStore.departureDate).format(API_DATE_FORMAT)
    : price.termsAndConditions?.departureDate || 0;
  const longDateFormat = t('productPriceTicketLine.departureDateFormat');
  const shortDateFormat = t('productPriceTicketLine.condensedDepartureDateFormat');
  const dValue = moment(departureDateValue, 'YYYYMMDD');
  const departureDate =
    type !== PRODUCT_TYPES.TOUR
      ? getPriceTicketItem({
          iconName: ICONS.DATE,
          label: t('productPriceTicketLine.departureDateLabel'),
          longDesc: `${dValue.format(longDateFormat)} - ${moment(dValue)
            .add(price.termsAndConditions?.duration || 0, 'days')
            .format(longDateFormat)}`,
          shortDesc: `${dValue.format(shortDateFormat)} - ${moment(dValue)
            .add(price.termsAndConditions?.duration || 0, 'days')
            .format(shortDateFormat)}`,
          subDesc: t('booking.form.selectDuration', {
            duration: price.termsAndConditions?.duration || 0,
          }),
        })
      : null;

  /* duration */
  const durationTour =
    type === PRODUCT_TYPES.TOUR
      ? getPriceTicketItem({
          iconName: ICONS.DURATION,
          longDesc: duration,
          shortDesc: duration,
        })
      : null;

  /* transport */
  const departureCityName =
    fromStore.departureCity || price.termsAndConditions?.departureCity?.name;
  const transport =
    departureCityName && departureCityName !== 'NO'
      ? getPriceTicketItem({
          iconName: product.transportType || ICONS.TRANSPORT,
          label: t('productPriceTicketLine.departureCityLabel'),
          longDesc: departureCityName,
        })
      : getPriceTicketItem({
          iconName: ICONS.NO_TRANSPORT,
          label: t('productPriceTicketLine.departureCityLabel'),
          longDesc: t('booking.form.withoutTransport'),
        });

  /* persons */
  const numberOfAdults =
    fromStore.adultsNumber || Number(price.termsAndConditions?.numberOfAdults) || 0;
  const personLabel =
    numberOfAdults > 1 ? t('productPriceTicketLine.adults') : t('productPriceTicketLine.adult');
  const persons = getPriceTicketItem({
    iconName: ICONS.PEOPLE_DOUBLE,
    label: numberOfAdults.toString(),
    longDesc: [numberOfAdults.toString(), personLabel].join(' ').trim(),
    shortDesc: numberOfAdults.toString(),
  });
  return [departureDate, durationTour, persons, transport].filter(Boolean);
};
